import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllWithdraw } from 'features/drivers/actions';
import { PaginationTable, Table, TableWrapper } from 'components/Global';
import EmptyState from 'components/Global/EmptyState';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { ReactComponent as DriverIcon } from 'icons/driving-wheel-icon.svg';
import { checkEmptyObject, setCurrency } from 'utils/functionality';
import FilterTransaction from 'components/Global/TransactionFilter';
import SearchBar from 'components/Global/TableWrapper/SearchBar';
import useDebounce from 'utils/useDebounce';
import { format, parseISO } from 'date-fns';

const column = [
  { header: 'No.', value: 'no' },
  { header: 'Nama', value: 'name' },
  { header: 'Email', value: 'email' },
  { header: 'No. Handphone', value: 'driver_phone' },
  { header: 'Nominal Withdraw', value: 'amount' },
  { header: 'Tanggal Request', value: 'created_at' },
  { header: 'Status', value: 'status' },
];

const FILTER_DATA = [
  {
    id: 'REQUEST',
    name: 'Belum Diproses',
    value: false,
  },
  {
    id: 'PROCESSED',
    name: 'Sedang Diproses',
    value: false,
  },
  {
    id: 'COMPLETED',
    name: 'Berhasil',
    value: false,
  },
  {
    id: 'REJECTED',
    name: 'Ditolak',
    value: false,
  },
];

const WithdrawList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: withdrawData, isLoading: withdrawDataLoading } = useSelector((state) => state.withdraw);

  const [currentPage, setCurrentPage] = useState(1);
  const [newWithdrawData, setNewWithdrawData] = useState([]);
  const [filterStatus, setFilterStatus] = useState(FILTER_DATA);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ count: 0, sortValue: '' });
  const [orderSeq, setOrderSeq] = useState('desc');

  const searchValueDebounce = useDebounce(search, 1000);

  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');

  const detailhandler = (e) => {
    const id = e.target.parentElement.parentElement.dataset.key;
    navigate(`/driver/withdraw?id=${id}`);
  };

  const sortByHandler = (sortValue) => {
    if (sortValue === sortBy.sortValue) {
      if (sortBy.count === 1) {
        setSortBy({ count: sortBy.count + 1, sortValue });
        setOrderSeq('desc');
        if (currentPage === 1) {
          dispatch(
            getAllWithdraw({
              page: 1,
              orderBy: sortValue,
              orderSeq: 'desc',
              name: search || undefined,
              limit: 10,
              status: status?.toLowerCase() !== 'all' ? status?.split(',') : undefined,
            }),
          );
        } else {
          setCurrentPage(1);
        }
      } else if (sortBy.count === 2) {
        setSortBy({ count: 0, sortValue: '' });
        setOrderSeq('desc');
        if (currentPage === 1) {
          dispatch(
            getAllWithdraw({
              page: 1,
              orderBy: '',
              orderSeq: '',
              name: search || undefined,
              limit: 10,
              status: status?.toLowerCase() !== 'all' ? status?.split(',') : undefined,
            }),
          );
        } else {
          setCurrentPage(1);
        }
      }
    } else {
      setSortBy({ count: 1, sortValue });
      setOrderSeq('asc');
      if (currentPage === 1) {
        dispatch(
          getAllWithdraw({
            page: 1,
            orderBy: sortValue,
            orderSeq: 'asc',
            name: search || undefined,
            limit: 10,
            status: status?.toLowerCase() !== 'all' ? status?.split(',') : undefined,
          }),
        );
      } else {
        setCurrentPage(1);
      }
    }
  };

  useEffect(() => {
    if (status || searchValueDebounce) {
      setCurrentPage(1);
      dispatch(
        getAllWithdraw({
          page: 1,
          orderSeq: sortBy.sortValue ? orderSeq : undefined,
          orderBy: sortBy.sortValue,
          status: status?.toLowerCase() !== 'all' ? status?.split(',') : undefined,
          name: searchValueDebounce || undefined,
          limit: 10,
        }),
      );
    }
  }, [status, searchValueDebounce]);

  useEffect(() => {
    dispatch(
      getAllWithdraw({
        page: currentPage,
        orderSeq: sortBy.sortValue ? orderSeq : undefined,
        orderBy: sortBy.sortValue,
        status: status?.toLowerCase() !== 'all' ? status?.split(',') : undefined,
        name: searchValueDebounce || undefined,
        limit: 10,
      }),
    );
  }, [currentPage]);

  useEffect(() => {
    if (!withdrawData || checkEmptyObject(withdrawData)) return;

    const gotFeeCurrency = (fee, currency) => {
      const RightFee =
        fee && currency === 'IDR'
          ? setCurrency(fee).replace('Rp', `${currency}`)
          : fee && currency === 'SGD'
          ? `${currency} ${fee.toFixed(2)}`
          : '-';
      return RightFee;
    };

    const mappingNewWithdrawData = withdrawData.data?.map((item) => ({
      id: item.id,
      name: item.name,
      email: item.email,
      driver_phone: item.phone,
      amount: gotFeeCurrency(item.amount, item.currency ? item.currency : ''),
      status: item.status,
      created_at: item.created_at ? format(parseISO(item.created_at), 'dd MMM yyyy') : '-',
    }));

    setNewWithdrawData(mappingNewWithdrawData);
  }, [withdrawData]);

  useEffect(() => {
    if (status?.toLowerCase() !== 'all') {
      setFilterStatus((prev) =>
        prev.map((item) =>
          status?.split(',').find((status) => status === item.id)
            ? { ...item, value: true }
            : { ...item, value: false },
        ),
      );
    } else if (status?.toLowerCase() == 'all') {
      setFilterStatus(FILTER_DATA);
    }
  }, [status]);

  const withdrawListView = () => {
    if (newWithdrawData?.length === 0 && !withdrawDataLoading) {
      return <EmptyState />;
    } else {
      return (
        <>
          <Table
            column={column}
            actionBtn
            onDetail={detailhandler}
            data={newWithdrawData}
            currentPage={currentPage}
            sort
            sortBy={sortBy}
            sortByHandler={sortByHandler}
            columnToSort={['Tanggal Request']}
          />
          <PaginationTable
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            totalCount={withdrawData?.pagination?.total}
          />
        </>
      );
    }
  };

  const customFilterView = (
    <div className="withdraw__custom-filter">
      <FilterTransaction
        selected={filterStatus}
        setPage={setCurrentPage}
        className="filter-status"
        placeholder="Filter"
        params={status}
        paramName="status"
      />
      <SearchBar
        type="text"
        value={search}
        placeholder="Search by Name"
        onChange={setSearch}
        style={{ marginLeft: 8 }}
      />
    </div>
  );

  return (
    <TableWrapper
      icon={<DriverIcon fill="#009EF7" width="25px" height="25px" />}
      title="Withdraw"
      CustomFilterComponent={true}
      style={{ minHeight: '700px' }}
      CustomSortComponent={customFilterView}
    >
      {withdrawDataLoading ? <LoadingSpinner /> : withdrawListView()}
    </TableWrapper>
  );
};

export default WithdrawList;
